<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { format } from 'date-fns';
import { fetchGetApi } from "@/util/api";

interface ReportEmployee {
    id: number;
    employeeId: string;
    name: string;
    status: string;
    department: string;
    siteName: string;
    type: string;
    post: string;
    operationManager: string;
    remainingDays: number;
    createdAt: string;
    endDate: string;
    attendance: {
        summary: {
            totalDaysAttended: number;
            totalDaysAbsent: number;
        };
    };
}

interface Statistics {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    recordsInCurrentPage: number;
    averageDaysRemaining: string;
    statusBreakdown: Record<string, number>;
    typeBreakdown: Record<string, number>;
}

const toast = useToast();
const employees = ref<ReportEmployee[]>([]);
const loading = ref(false);
const statistics = ref<Statistics | null>(null);

// Filter states
const filters = ref({
    siteId: '',
    siteName: '',
    employeeName: '',
    department: '',
    manager: '',
    status: '',
    type: '',
    employeeType: '',
    minDaysRemaining: '',
    maxDaysRemaining: '',
    startDate: '',
    endDate: '',
    sortBy: 'RemainingDaysOfChase',
    sortOrder: 'asc'
});

const currentPage = ref(1);
const pageLimit = ref(20);
const totalRecords = ref(0);

const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy');
};

const calculateEndDate = (createdAt: string, remainingDays: number) => {
    if (!createdAt) return '';
    const startDate = new Date(createdAt);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + remainingDays);
    return endDate.toISOString();
};

const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
        'Active': 'badge bg-success',
        'Inactive': 'badge bg-danger',
        'In Progress': 'badge bg-warning',
        'Pending': 'badge bg-info'
    };
    return classes[status] || 'badge bg-secondary';
};

const fetchReport = async () => {
    loading.value = true;
    try {
        // Build query parameters
        const params = new URLSearchParams();
        
        Object.entries(filters.value).forEach(([key, value]) => {
            if (value) {
                params.append(key, value.toString());
            }
        });
        
        params.append('page', currentPage.value.toString());
        params.append('limit', pageLimit.value.toString());

        const queryString = params.toString();
        const endpoint = `api/admin/get/chase/list/report${queryString ? '?' + queryString : ''}`;
        
        const response = await fetchGetApi(endpoint);
        
        if (response?.data?.success && response.data.data) {
            employees.value = response.data.data.map((item: any) => {
                const createdAt = item.createdAt || item.CreatedAt || '';
                const remainingDays = item.RemainingDaysOfChase || 0;
                return {
                    id: item._id,
                    employeeId: item.empId,
                    name: item.Name,
                    status: item.Status || 'N/A',
                    department: item.Dept,
                    type: item.Type,
                    siteName: item.Site || 'N/A',
                    post: item.Post,
                    operationManager: item.OperationManagerName,
                    remainingDays: remainingDays,
                    createdAt: createdAt,
                    endDate: calculateEndDate(createdAt, remainingDays),
                    attendance: {
                        summary: {
                            totalDaysAttended: item.attendance?.summary?.totalDaysAttended || 0,
                            totalDaysAbsent: item.attendance?.summary?.totalDaysAbsent || 0
                        }
                    }
                };
            });
            
            // Store statistics
            if (response.data.statistics) {
                statistics.value = response.data.statistics;
                totalRecords.value = response.data.statistics.totalRecords;
            } else {
                totalRecords.value = employees.value.length;
            }
        } else {
            toast.error('Failed to fetch report data');
        }
    } catch (error) {
        console.error('Error fetching chase list report:', error);
        toast.error('Failed to fetch chase list report');
    } finally {
        loading.value = false;
    }
};

const resetFilters = () => {
    filters.value = {
        siteId: '',
        siteName: '',
        employeeName: '',
        department: '',
        manager: '',
        status: '',
        type: '',
        employeeType: '',
        minDaysRemaining: '',
        maxDaysRemaining: '',
        startDate: '',
        endDate: '',
        sortBy: 'RemainingDaysOfChase',
        sortOrder: 'asc'
    };
    currentPage.value = 1;
};

const changePage = (page: number) => {
    currentPage.value = page;
    fetchReport();
};

// Watch filters for automatic application
watch(filters, () => {
    currentPage.value = 1;
    fetchReport();
}, { deep: true });

onMounted(() => {
    fetchReport();
});
</script>

<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-header">
                <div class="row align-items-center">
                    <div class="col">
                        <h5>Chase List Report</h5>
                    </div>
                    <div class="col-auto">
                        <router-link to="/chase-list/list" class="btn btn-secondary btn-sm">
                            <i class="fa fa-arrow-left me-1"></i> Back to Chase List
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Filters Section -->
            <div class="card-body border-bottom">
                <div class="row g-3">
                    <div class="col-md-3">
                        <label class="form-label">Employee Name</label>
                        <input type="text" class="form-control form-control-sm" v-model="filters.employeeName" placeholder="Search by name">
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Site Name</label>
                        <input type="text" class="form-control form-control-sm" v-model="filters.siteName" placeholder="Search by site">
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Status</label>
                        <select class="form-select form-select-sm" v-model="filters.status">
                            <option value="">All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="In Progress">In Progress</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Type</label>
                        <select class="form-select form-select-sm" v-model="filters.type">
                            <option value="">All Types</option>
                            <option value="Joining">Joining</option>
                            <option value="Reinstate">Reinstate</option>
                        </select>
                    </div>
                
                   
                    <div class="col-md-3">
                        <label class="form-label">Start Date</label>
                        <input type="date" class="form-control form-control-sm" v-model="filters.startDate">
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">End Date</label>
                        <input type="date" class="form-control form-control-sm" v-model="filters.endDate">
                    </div>
                  
                    <div class="col-md-3">
                        <label class="form-label">Sort Order</label>
                        <select class="form-select form-select-sm" v-model="filters.sortOrder">
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </div>
                    <div class="col-12">
                        <button class="btn btn-secondary btn-sm" @click="resetFilters">
                            <i class="fa fa-refresh me-1"></i> Reset Filters
                        </button>
                    </div>
                </div>
            </div>

        

            <!-- Table Section -->
            <div class="card-body">
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-bordered table-striped">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Dept.</th>
                                <th>Type</th>
                                <th>Site Name</th>
                                <th>Post</th>
                                <th>Days Attended</th>
                                <th>Operation Manager</th>
                                <th>Remaining Days</th>
                                <th>Joining Date</th>
                                <th>End Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="employee in employees" :key="employee.id">
                                <td>{{ employee.name }}</td>
                                <td>
                                    <span :class="getStatusClass(employee.status)">
                                        {{ employee.status }}
                                    </span>
                                </td>
                                <td>{{ employee.department }}</td>
                                <td :style="employee.type === 'Siddar' ? 'color: red; background-color: rgba(255,0,0,0.1)' : ''">
                                    {{ employee.type }}
                                </td>
                                <td>{{ employee.siteName }}</td>
                                <td>{{ employee.post }}</td>
                                <td>{{ employee.attendance.summary.totalDaysAttended }}</td>
                                <td>{{ employee.operationManager }}</td>
                                <td>{{ employee.remainingDays }}</td>
                                <td>{{ employee.createdAt ? formatDate(employee.createdAt) : 'N/A' }}</td>
                                <td>{{ employee.endDate ? formatDate(employee.endDate) : 'N/A' }}</td>
                            </tr>
                            <tr v-if="employees.length === 0">
                                <td colspan="11" class="text-center text-muted">No records found</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="employees.length > 0" class="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        <small class="text-muted">
                            Showing {{ ((currentPage - 1) * pageLimit) + 1 }} to {{ Math.min(currentPage * pageLimit, totalRecords) }} of {{ totalRecords }} records
                            <span v-if="statistics"> | Page {{ statistics.currentPage }} of {{ statistics.totalPages }}</span>
                        </small>
                    </div>
                    <div>
                        <button class="btn btn-sm btn-outline-primary me-2" 
                                @click="changePage(currentPage - 1)" 
                                :disabled="currentPage === 1">
                            Previous
                        </button>
                        <span class="me-2">Page {{ currentPage }}</span>
                        <button class="btn btn-sm btn-outline-primary" 
                                @click="changePage(currentPage + 1)" 
                                :disabled="statistics ? currentPage >= statistics.totalPages : employees.length < pageLimit">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.card {
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.table th {
    background-color: #f8f9fa;
    white-space: nowrap;
    font-size: 0.9rem;
}

.table td {
    font-size: 0.9rem;
}

.badge {
    padding: 0.5em 0.75em;
}

.form-label {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
}

.stat-card {
    padding: 0.75rem;
    background: white;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;
}

.stat-card h5 {
    color: #0d6efd;
    font-weight: 600;
}

.stat-card small {
    display: block;
    margin-bottom: 0.25rem;
}
</style>
